import { get, patch, post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import {
  type CreateShiftTimeProposalRequest,
  type CreateShiftTimeProposalResponse,
  type GetShiftTimeProposalByIdResponse,
  type GetShiftTimeProposalsResponse,
  type ShiftTimeProposalStatus,
  type UpdateShiftTimeProposalRequest,
  type UpdateShiftTimeProposalResponse,
} from "../types";
import {
  createShiftTimeProposalRequestSchema,
  createShiftTimeProposalResponseSchema,
  getShiftTimeProposalByIdResponseSchema,
  getShiftTimeProposalsResponseSchema,
  updateShiftTimeProposalRequestSchema,
  updateShiftTimeProposalResponseSchema,
} from "./schema";

const ShiftTimeProposalsQueryKey = "shift-time-proposals";

interface GetShiftTimeProposalsParams {
  shiftId: string;
  status?: ShiftTimeProposalStatus[];
}

interface GetShiftTimeProposalByIdParams {
  shiftTimeProposalId: string;
}

export function useGetShiftTimeProposals(
  params: GetShiftTimeProposalsParams,
  options: UseQueryOptions<GetShiftTimeProposalsResponse> = {}
) {
  const { shiftId, status } = params;

  return useQuery<GetShiftTimeProposalsResponse>({
    queryKey: [ShiftTimeProposalsQueryKey, shiftId],
    queryFn: async () => {
      const filter: Record<string, unknown> = {
        "shift.id": shiftId,
      };

      if (status) {
        filter.status = status.join(",");
      }

      const queryParams = {
        filter,
      };

      const { data } = await get({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/shift-time-proposals`,
        responseSchema: getShiftTimeProposalsResponseSchema,
        queryParams,
      });

      return data;
    },
    ...options,
  });
}

export function useGetShiftTimeProposalById(
  params: GetShiftTimeProposalByIdParams,
  options: UseQueryOptions<GetShiftTimeProposalByIdResponse> = {}
) {
  const { shiftTimeProposalId } = params;
  return useQuery<GetShiftTimeProposalByIdResponse>({
    queryKey: [ShiftTimeProposalsQueryKey, shiftTimeProposalId],
    queryFn: async () => {
      const { data } = await get({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/shift-time-proposals/${shiftTimeProposalId}`,
        responseSchema: getShiftTimeProposalByIdResponseSchema,
      });

      return data;
    },
    ...options,
  });
}

export function useCreateShiftTimeProposal(
  options: UseMutationOptions<
    CreateShiftTimeProposalResponse,
    unknown,
    CreateShiftTimeProposalRequest
  > = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request) => {
      const { data } = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/shift-time-proposals`,
        requestSchema: createShiftTimeProposalRequestSchema,
        responseSchema: createShiftTimeProposalResponseSchema,
        data: request,
      });

      await queryClient.invalidateQueries({
        queryKey: [ShiftTimeProposalsQueryKey, data.data.relationships.shift.data.id],
      });

      return data;
    },
    ...options,
  });
}

export function useUpdateShiftTimeProposal(
  options: UseMutationOptions<
    UpdateShiftTimeProposalResponse,
    unknown,
    UpdateShiftTimeProposalRequest & { id: string }
  > = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...request }) => {
      const { data } = await patch({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/shift-time-proposals/${id}`,
        requestSchema: updateShiftTimeProposalRequestSchema,
        responseSchema: updateShiftTimeProposalResponseSchema,
        data: request,
      });

      await queryClient.invalidateQueries({
        queryKey: [ShiftTimeProposalsQueryKey, data.data.relationships.shift.data.id],
      });

      return data;
    },
    ...options,
  });
}
